(() => {
  const REVEAL_SELECTOR = '[data-rotar-reveal]';
  const FAQ_SELECTOR = '[data-rotar-faq]';
  const FAQ_ITEM_SELECTOR = '[data-rotar-faq-item]';
  const FAQ_PANEL_SELECTOR = '[data-rotar-faq-panel]';
  const FAQ_ANIMATION_DURATION = 280;
  const faqAnimations = new WeakMap();

  const show = (element) => element.classList.add('is-visible');

  const prepare = (scope = document) => {
    const elements = [...scope.querySelectorAll(REVEAL_SELECTOR)];

    if (
      elements.length === 0 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      elements.forEach(show);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.14,
        rootMargin: '0px 0px -6% 0px',
      }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const animateFaqItem = (item, expand, immediate = false) => {
    const panel = item.querySelector(FAQ_PANEL_SELECTOR);
    const activeRecord = faqAnimations.get(item);
    const startHeight = panel?.getBoundingClientRect().height ?? 0;

    activeRecord?.animation.cancel();

    if (
      !panel ||
      immediate ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof panel.animate !== 'function'
    ) {
      item.open = expand;
      faqAnimations.delete(item);
      delete item.dataset.rotarFaqState;
      return Promise.resolve();
    }

    if (expand && !item.open) item.open = true;
    if (!expand && !item.open) return Promise.resolve();

    const endHeight = expand ? panel.scrollHeight : 0;
    const fromHeight = expand && startHeight < 1 ? 0 : startHeight;
    const fullHeight = Math.max(panel.scrollHeight, 1);
    const fromOpacity = Math.min(fromHeight / fullHeight, 1);

    if (Math.abs(fromHeight - endHeight) < 1) {
      item.open = expand;
      faqAnimations.delete(item);
      delete item.dataset.rotarFaqState;
      return Promise.resolve();
    }

    item.dataset.rotarFaqState = expand ? 'opening' : 'closing';

    const animation = panel.animate(
      [
        { height: `${fromHeight}px`, opacity: fromOpacity },
        { height: `${endHeight}px`, opacity: expand ? 1 : 0 },
      ],
      {
        duration: FAQ_ANIMATION_DURATION,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      }
    );
    const record = { animation, expand };

    faqAnimations.set(item, record);

    return animation.finished
      .catch(() => undefined)
      .then(() => {
        if (faqAnimations.get(item) !== record) return;

        item.open = expand;
        faqAnimations.delete(item);
        delete item.dataset.rotarFaqState;
      });
  };

  const prepareFaq = (scope = document) => {
    const faqRoots = [
      ...(scope.matches?.(FAQ_SELECTOR) ? [scope] : []),
      ...scope.querySelectorAll(FAQ_SELECTOR),
    ];

    faqRoots.forEach((faqRoot) => {
      if (faqRoot.dataset.rotarFaqReady === 'true') return;

      const items = [...faqRoot.querySelectorAll(FAQ_ITEM_SELECTOR)];
      const initiallyOpen = items.filter((item) => item.open);
      let interactionId = 0;

      initiallyOpen.slice(1).forEach((item) => item.removeAttribute('open'));

      items.forEach((item) => {
        const summary = item.querySelector(':scope > summary');

        summary?.addEventListener('click', async (event) => {
          event.preventDefault();

          const activeRecord = faqAnimations.get(item);
          const shouldOpen = activeRecord ? !activeRecord.expand : !item.open;
          const currentInteractionId = ++interactionId;

          if (shouldOpen) {
            const openSiblings = items.filter(
              (otherItem) =>
                otherItem !== item &&
                (otherItem.open || faqAnimations.get(otherItem)?.expand)
            );

            await Promise.all(
              openSiblings.map((otherItem) => animateFaqItem(otherItem, false))
            );

            if (currentInteractionId !== interactionId) return;
          }

          await animateFaqItem(item, shouldOpen);
        });
      });

      faqRoot.dataset.rotarFaqReady = 'true';
    });
  };

  const initialize = (scope = document) => {
    prepare(scope);
    prepareFaq(scope);
  };

  document.documentElement.classList.add('rotar-motion-ready');
  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
